import type { MouseEvent, ReactNode } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { Button, Drawer, Spin, Typography } from "antd";
import clsx from "clsx";
import "./journal-record-preview-panel.scss";

const INTERACTIVE_SELECTOR =
  "button, a, input, textarea, select, label, .ant-checkbox, .ant-dropdown, .ant-dropdown-trigger, .ant-dropdown-menu, .ant-table-selection-column, .journal-table-dropdown-trigger";

export const isTablePreviewClick = (event: MouseEvent<HTMLElement>) => {
  const target = event.target as HTMLElement | null;
  return !target?.closest(INTERACTIVE_SELECTOR);
};

type JournalRecordPreviewInfoRowProps = {
  label: string;
  children: React.ReactNode;
  alignTop?: boolean;
  className?: string;
};

export const JournalRecordPreviewInfoRow = ({
  label,
  children,
  alignTop,
  className,
}: JournalRecordPreviewInfoRowProps) => (
  <div
    className={clsx(
      "journal-record-preview-info-row",
      alignTop && "journal-record-preview-info-row--align-top",
      className,
    )}
  >
    <Typography.Text type="secondary" className="journal-record-preview-info-row__label">
      {label}
    </Typography.Text>
    <div className="journal-record-preview-info-row__value">{children}</div>
  </div>
);

type JournalRecordPreviewPanelProps = {
  open: boolean;
  onClose: () => void;
  sequenceNumber?: number | string;
  statusBadge?: ReactNode;
  isLoading?: boolean;
  onOpenRecord?: () => void;
  openRecordLabel?: string;
  children?: ReactNode;
};

export const JournalRecordPreviewPanel = ({
  open,
  onClose,
  sequenceNumber,
  statusBadge,
  isLoading,
  onOpenRecord,
  openRecordLabel = "Открыть запись",
  children,
}: JournalRecordPreviewPanelProps) => (
  <Drawer
    open={open}
    onClose={onClose}
    placement="right"
    width={385}
    closable={false}
    className="journal-record-preview-panel"
    title={null}
  >
    <div className="journal-record-preview-panel__content">
      <div className="journal-record-preview-panel__header">
        <div className="journal-record-preview-panel__header-main">
          <Typography.Text className="journal-record-preview-panel__label">
            Выбранная запись
          </Typography.Text>
          <div className="journal-record-preview-panel__title-row">
            <Typography.Title level={4} className="journal-record-preview-panel__title">
              №{sequenceNumber ?? "-"}
            </Typography.Title>
            {statusBadge}
          </div>
        </div>
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={onClose}
          className="journal-record-preview-panel__close"
        />
      </div>

      <Typography.Title level={4} className="journal-record-preview-panel__info-title">
        Информация
      </Typography.Title>

      <div className="journal-record-preview-panel__body">
        {isLoading ? (
          <div className="journal-record-preview-panel__loading">
            <Spin />
          </div>
        ) : (
          children
        )}
      </div>

      {onOpenRecord ? (
        <Button
          type="primary"
          className="journal-record-preview-panel__open-record"
          onClick={onOpenRecord}
        >
          {openRecordLabel}
        </Button>
      ) : null}
    </div>
  </Drawer>
);
